// Grids & Gantts — the compact wire shape a server-built structure travels in.
//
// A structure of thirty thousand work items used to reach the browser as
// thirty thousand Jira-shaped objects: every one carrying its own copy of
// the project (with avatar URLs), the status (with its category), the work
// type (with icon URLs), every user with an avatar set and an email, and
// every link with a nested issue object — 75 MB of JSON per open, measured,
// of which the page read perhaps a tenth. This is the tenth.
//
// KEPT: what a column, a dropdown filter, a grouping, the engine (the
// Parent key, the links), the timeline (the dates), editing (the ids) and
// cloning (the type ids) read. DROPPED: avatars, self links, emails, the
// per-item copies of status / type / project — those are sent ONCE, in a
// dictionary, and referenced by id.
//
// Pure, import-free, and the SAME file on both sides: the server compacts
// with it (imported as ESM from a data: URL) and the page inflates with
// it, so the two cannot drift. The suite proves a round trip keeps what
// the columns read.

const isObj = (v) => v && typeof v === 'object' && !Array.isArray(v);

// A field value with the fat taken off: a user is its id and name, an
// option its id and value (and its cascading child), an array each of its
// members; a string, a number or a date is itself.
export function slimValue(v) {
  if (v == null) return v;
  if (Array.isArray(v)) return v.map(slimValue);
  if (!isObj(v)) return v;
  if (v.accountId !== undefined) return { accountId: v.accountId, displayName: v.displayName || v.name || null };
  const o = {};
  if (v.id !== undefined) o.id = v.id;
  if (v.value !== undefined) o.value = v.value;
  if (v.name !== undefined && v.value === undefined) o.name = v.name;
  if (v.key !== undefined) o.key = v.key;
  if (v.child) o.child = slimValue(v.child);
  return Object.keys(o).length ? o : v;
}

// Always carried, whatever the columns ask for: the tree cannot be drawn
// or sorted without them.
export const CORE_FIELDS = ['summary', 'duedate'];
// Built from the dictionary or the tree, never copied per item.
const STRUCTURAL = new Set(['status', 'statusCategory', 'issuetype', 'project', 'parent', 'issuelinks',
  'comment', 'attachment', 'worklog', 'watches', 'subtasks']);

export function newDict() { return { statuses: {}, types: {}, projects: {} }; }

export function compactIssue(issue, wanted, dict) {
  const f = (issue && issue.fields) || {};
  const st = f.status; const wt = f.issuetype; const pr = f.project;
  if (st && st.id != null && !dict.statuses[st.id]) {
    dict.statuses[st.id] = {
      id: String(st.id), name: st.name || '',
      statusCategory: st.statusCategory ? { id: st.statusCategory.id != null ? st.statusCategory.id : null, key: st.statusCategory.key || null, name: st.statusCategory.name || null } : null,
    };
  }
  if (wt && wt.id != null && !dict.types[wt.id]) {
    dict.types[wt.id] = { id: String(wt.id), name: wt.name || '', iconUrl: wt.iconUrl || null, subtask: !!wt.subtask, hierarchyLevel: wt.hierarchyLevel != null ? wt.hierarchyLevel : null };
  }
  if (pr && pr.id != null && !dict.projects[pr.id]) {
    dict.projects[pr.id] = {
      id: String(pr.id), key: pr.key || null, name: pr.name || null,
      // ONE small avatar per PROJECT, in the dictionary (so once per project,
      // never per item) — what a "Group by Project" header wears. User
      // avatars stay dropped.
      avatar: (pr.avatarUrls && (pr.avatarUrls['16x16'] || pr.avatarUrls['24x24'] || pr.avatarUrls['48x48'])) || pr.avatar || null,
      projectCategory: pr.projectCategory ? { id: pr.projectCategory.id != null ? pr.projectCategory.id : null, name: pr.projectCategory.name || null } : null,
    };
  }
  const out = {
    key: issue.key, id: issue.id != null ? String(issue.id) : null,
    s: st && st.id != null ? String(st.id) : null,
    t: wt && wt.id != null ? String(wt.id) : null,
    p: pr && pr.id != null ? String(pr.id) : null,
    f: {},
  };
  if (f.parent && f.parent.key) out.parent = f.parent.key;
  if (f.parent && f.parent.id) out.parentId = String(f.parent.id);
  const links = Array.isArray(f.issuelinks) ? f.issuelinks : [];
  if (links.length) {
    out.l = links.map((l) => {
      const t = (l && l.type) || {};
      const other = l && (l.inwardIssue || l.outwardIssue);
      return [t.name || '', t.inward || '', t.outward || '', (other && other.key) || '', l && l.inwardIssue ? 'i' : 'o', l && l.id != null ? String(l.id) : ''];
    }).filter((x) => x[3]);
  }
  const ids = new Set([...CORE_FIELDS, ...(wanted || [])]);
  ids.forEach((id) => {
    if (STRUCTURAL.has(id) || f[id] === undefined) return;
    out.f[id] = slimValue(f[id]);
  });
  return out;
}

export function inflateIssue(c, dict) {
  const st = c.s != null ? dict.statuses[c.s] || null : null;
  const wt = c.t != null ? dict.types[c.t] || null : null;
  const pr = c.p != null ? dict.projects[c.p] || null : null;
  const fields = {
    ...(c.f || {}),
    status: st, statusCategory: st ? st.statusCategory : null, issuetype: wt, project: pr,
    parent: c.parent ? { key: c.parent, id: c.parentId || null } : null,
    issuelinks: (c.l || []).map(([name, inward, outward, key, side, id]) => {
      const o = { id: id || null, type: { name, inward: inward || null, outward: outward || null } };
      if (side === 'i') o.inwardIssue = { key }; else o.outwardIssue = { key };
      return o;
    }),
  };
  return { id: c.id, key: c.key, fields };
}

// The server's half: parts with Maps → the wire. Items are sent once,
// however many folders hold them.
export function deflate(parts, wanted) {
  const dict = newDict();
  const items = {};
  const out = (parts || []).map((p) => {
    const keys = [];
    p.byKey.forEach((it, k) => {
      keys.push(k);
      if (!items[k]) items[k] = compactIssue(it, wanted, dict);
    });
    const kidsOf = {};
    p.kidsOf.forEach((kids, pk) => { kidsOf[pk] = kids.slice(); });
    return { folderId: p.folder ? p.folder.id : p.folderId, roots: (p.roots || []).map((r) => r.key), keys, kidsOf, depth: p.depth || 0 };
  });
  return { dict, items, parts: out };
}

// The page's half: the wire → parts with Maps, every item inflated ONCE and
// shared between the folders that hold it.
export function inflate(payload) {
  const dict = (payload && payload.dict) || newDict();
  const items = new Map();
  Object.keys((payload && payload.items) || {}).forEach((k) => items.set(k, inflateIssue(payload.items[k], dict)));
  const parts = ((payload && payload.parts) || []).map((p) => {
    const byKey = new Map();
    (p.keys || []).forEach((k) => { const it = items.get(k); if (it) byKey.set(k, it); });
    const kidsOf = new Map();
    Object.keys(p.kidsOf || {}).forEach((pk) => kidsOf.set(pk, (p.kidsOf[pk] || []).slice()));
    return { folderId: p.folderId, roots: (p.roots || []).map((k) => items.get(k)).filter(Boolean), byKey, kidsOf, depth: p.depth || 0 };
  });
  return { parts, items, dict };
}
